import { action, extend, getRoot, register, types, type Instance, type SnapshotOrInstance } from "@gadgetinc/mobx-quick-tree";
import { EnvironmentConfig } from "../../EnvironmentConfig";
import { ShopifyAdminAppConfig } from "./ShopifyAdminAppConfig";
import { ShopifyBaseAdminConnectionConfig } from "./ShopifyBaseAdminConfig";

/**
 * See packages/state-trees/src/connections/ShopifyConnectionConfig.ts for documentation
 * on the different types of Shopify configurations
 */
@register
export class ShopifyAdminConnectionConfig extends extend(
  ShopifyBaseAdminConnectionConfig("ShopifyAdminConnectionConfig", ShopifyAdminAppConfig),
  {
    environmentConfigs: types.map(types.reference(EnvironmentConfig)),
    lastShopInstallEventAt: types.maybe(types.number),
  }
) {
  get environmentConfigsAsArray(): Instance<typeof EnvironmentConfig>[] {
    return Array.from(this.environmentConfigs.values());
  }

  get appConfigs() {
    const appConfigValues = this.environmentConfigsAsArray.flatMap((ec) =>
      ShopifyAdminAppConfig.is(ec.configuration) ? [ec.configuration] : []
    );

    return new Map(appConfigValues.map((ac) => [ac.key, ac]));
  }

  get appConfigsAsArray(): ShopifyAdminAppConfig[] {
    return Array.from(this.appConfigs.values());
  }

  @action
  putEnvironmentConfig(config: Instance<typeof EnvironmentConfig>): Instance<typeof EnvironmentConfig> {
    this.environmentConfigs.put(config);

    return config;
  }
  @action
  putAppConfig(config: SnapshotOrInstance<typeof ShopifyAdminAppConfig>): ShopifyAdminAppConfig {
    const root = getRoot(this);

    const environmentConfig = root.addNewEnvironmentConfig({ configuration: config });

    this.environmentConfigs.put(environmentConfig);

    return environmentConfig.configuration;
  }
  @action
  removeAppConfig(configKey: string): boolean {
    const environmentConfig = this.environmentConfigsAsArray.find((ec) => ec.configuration.key === configKey);

    if (!environmentConfig) {
      throw new Error(`No environment config found for app config ${configKey}`);
    }

    const envConfigKey = environmentConfig.key;

    return this.environmentConfigs.delete(envConfigKey);
  }

  @action
  removeEnvironmentConfig(configKey: string): boolean {
    return this.environmentConfigs.delete(configKey);
  }

  @action
  setLastShopInstallEventAt(lastShopInstallEventAt: number) {
    this.lastShopInstallEventAt = lastShopInstallEventAt;
  }
}
